import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, MenuItem } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { ActionType } from "src/constants/table";
import { UserRoles } from "src/constants/user";
import { ChevronDown, ChevronUp, EyeOpenedMenuIcon, PenMenuIcon, TrashBinMenuIcon } from "src/Icons";
import { setWorkingTimeDialogParams, showWorkingTimeDialog } from "src/store/reducers/dialogSlice";
import { openDrawer } from "src/store/reducers/drawerSlice";
import { closeModal, showModal } from "src/store/reducers/modalSlice";
import { getSchedules, removeSchedule } from "src/store/reducers/schedulesSlice";
import { setSelectedRow } from "src/store/reducers/tableSlice";
import { ActionButton } from "src/styledComponents/ActionButton";
import { ActionMenu } from "src/styledComponents/ActionMenu";

const CustomActionsCell = ({ params }: any) => {
  const { user } = useSelector((state: any) => state.auth);
  // @ts-ignore
  const userRole = UserRoles[user?.role];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const dispatch = useDispatch();
  const handleAction = (action: ActionType) => {
    return () => {
      switch (action) {
        case ActionType.view:
          dispatch(setSelectedRow(params.row));
          dispatch(openDrawer());
          console.log("should View");
          break;
        case ActionType.edit:
          dispatch(
            setWorkingTimeDialogParams({
              step: 0,
              maxAvailableStep: 1,
              isEdit: true,
              data: params.row,
            })
          );
          dispatch(showWorkingTimeDialog());
          console.log("should edit");
          break;
        case ActionType.delete:
          dispatch(
            showModal({
              modalType: "DELETE",
              headerTitle: "Are you sure?",
              title: ` Please , confirm your decision with deletion of the ${params?.row?.title}`,
              subTitle:
                  "Deleting this schedule will result in the following consequences:",
              description:
                  "Studios assigned exclusively to this schedule template will be unassigned and need to be reassigned to other template. \n Any customized settings and preferences specific to this schedule template will be lost.",
              okButtonTitle: "Yes, delete schedule",
              onButtonClick: () => {
                dispatch(closeModal());
                dispatch(
                  removeSchedule({
                    currentId: params.id,
                    onSuccess: () => {
                      dispatch(
                        showModal({
                          modalType: "SUCCESS",
                          successText:
                              "Schedule template is successfully deleted",
                          okButtonTitle: "Ok, I got it",
                          onButtonClick: () => {
                            dispatch(getSchedules());
                            dispatch(closeModal());
                          },
                        })
                      );
                    },
                    onFailure: (error: any) => {
                      dispatch(
                        showModal({
                          modalType: "DELETE",
                          title: `${error?.response?.data?.detail || error.message}`,
                          okButtonTitle: "Ok, I got it",
                          isButtonCancel: true,
                          onButtonClick: () => {
                            dispatch(closeModal());
                          },
                        })
                      );
                    },
                  })
                );
              },
              onButtonCancelClick: () => {
                dispatch(closeModal());
              },
            })
          );
          console.log("should Delete");
          break;
        default:
          throw new Error("Not proper ActionType");
      }
      setAnchorEl(null);
    };
  };

  return (
    <div>
      <ActionButton
        id="action-button"
        variant="outlined"
        aria-controls={open ? "action-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        // eslint-disable-next-line react/jsx-no-undef
        endIcon={open ? <ChevronUp /> : <ChevronDown />}
        disableRipple
      >
          Actions
      </ActionButton>
      <ActionMenu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "action-button",
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem onClick={handleAction(ActionType.view)} disableRipple>
          <EyeOpenedMenuIcon />
            View
        </MenuItem>
        {userRole === UserRoles.SUPER_ADMIN &&
        <MenuItem onClick={handleAction(ActionType.edit)} disableRipple>
          <PenMenuIcon />
            Edit
        </MenuItem>
        }
        <MenuItem onClick={handleAction(ActionType.delete)} disableRipple>
          <TrashBinMenuIcon />
            Delete
        </MenuItem>
      </ActionMenu>
    </div>
  );
};

const CustomStringCell = ({ params }: any) => {
  return (
    <Box display="flex" alignItems="center">
      {params.value}
    </Box>
  );
};

const CustomLastUpdateCell = ({ params }: any) => {
  const normalizedTimeStr = moment(params.value).fromNow();

  return (
    <Box display="flex" alignItems="center">
      {normalizedTimeStr}
    </Box>
  );
};

export const columns: GridColDef[] = [
  {
    field: "id",
    headerName: "Id",
    width: 100,
    sortable: false,
  },
  {
    field: "title",
    headerName: "Name",
    width: 275,
    sortable: false,
    renderCell: (params) => <CustomStringCell params={params} />,
  },
  {
    field: "description",
    headerName: "Description",
    width: 230,
    sortable: false,
    renderCell: (params) => <CustomStringCell params={params} />,
  },
  {
    field: "updatedAt",
    headerName: "Last update",
    width: 230,
    flex: 1,
    sortable: false,
    renderCell: (params) => <CustomLastUpdateCell params={params as any} />,
  },
  {
    field: "actions",
    headerName: "Actions",
    minWidth: 120,
    sortable: false,
    renderCell: (params) => <CustomActionsCell params={params} />,
  },
];
